<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="viewOnly"
    @dismiss-changes="$emit('closeModal')"
    @save="onSave">
    <template #form>
      <div class="space-y-4" :class="{ 'pointer-events-none': viewOnly }">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{ $t('baseObject.code') }}</label>
          <BaseInput
            v-model="localImport.code"
            color="light"
            :disabled="viewOnly"
            :placeholder="$t('baseObject.code')"
            type="text"
            :error="$v.code.required.$invalid ? $v.code.required.$message : ''"
            show-error-as-tooltip />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{ $t('baseObject.name') }}</label>
          <BaseInput
            v-model="localImport.name"
            color="light"
            :disabled="viewOnly"
            :placeholder="$t('baseObject.name')"
            type="text" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{
            $t('representative.remarks')
          }}</label>
          <textarea
            v-model="localImport.remarks"
            :disabled="viewOnly"
            :placeholder="$t('representative.remarks')"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"></textarea>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{
            $t('representative.importFolder')
          }}</label>
          <BaseInput
            v-model="localImport.importFolder"
            color="light"
            :disabled="viewOnly"
            :placeholder="$t('representative.importFolder')"
            type="text" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{
            $t('representative.importUrl')
          }}</label>
          <BaseInput
            v-model="localImport.importUrl"
            color="light"
            :disabled="viewOnly"
            :placeholder="$t('representative.importUrl')"
            type="text" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{
            $t('representative.importFileNameFormat')
          }}</label>
          <BaseInput
            v-model="localImport.importFileNameFormat"
            color="light"
            :disabled="viewOnly"
            :placeholder="$t('representative.importFileNameFormat')"
            type="text" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">{{
            $t('representative.lastImportDate')
          }}</label>
          <DateTimePicker
            ref="lastImportDatePickerRef"
            v-model="selectedLastImportDate"
            :placeholder="$t('representative.lastImportDate')"
            :show-time-picker="false"
            :disabled="viewOnly"
            start-empty
            show-error-as-tooltip
            @update:model-value="onDateChange" />
        </div>
        <div class="space-y-2" :class="{ 'pointer-events-none': viewOnly }">
          <BaseToggle
            v-model="localImport.isActive"
            :disable="viewOnly"
            :label="$t('baseObject.isActive')"
            name="isActive" />
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';

import { RecordMode } from '@/types/Base';
import type { DCRepresentativeImport } from '@/types/DataCollection';
import { deepCopy } from '@/utils/data';
import { isoDateToDatepickerFormat, datepickerToServerDateFormat } from '@/utils/date';

const { t: $t } = useI18n();

const props = defineProps<{
  representativeImport: DCRepresentativeImport;
  mode: 'add' | 'edit' | 'view';
  representativeId: string;
  viewOnly?: boolean;
}>();

const emit = defineEmits<{
  closeModal: [];
  save: [DCRepresentativeImport];
}>();

const localImport = ref<DCRepresentativeImport>(deepCopy(props.representativeImport));
const selectedLastImportDate = ref<string>('');
const lastImportDatePickerRef = ref();

const isModified = computed(() => {
  return JSON.stringify(localImport.value) !== JSON.stringify(props.representativeImport);
});

watch(
  () => props.representativeImport,
  (newValue) => {
    localImport.value = deepCopy(newValue);
    selectedLastImportDate.value = newValue.lastImportDate
      ? isoDateToDatepickerFormat(newValue.lastImportDate)
      : '';
  },
  { deep: true, immediate: true }
);

function onDateChange() {
  localImport.value.lastImportDate = datepickerToServerDateFormat(selectedLastImportDate.value);
  validationModel.value.lastImportDate = selectedLastImportDate.value;
}

function onSave() {
  $v.value.$validate();
  if ($v.value.$invalid) return;
  if (props.mode === 'add') {
    localImport.value.recordMode = RecordMode.Added;
  } else if (localImport.value.recordMode !== RecordMode.Added) {
    localImport.value.recordMode = RecordMode.Modified;
  }
  localImport.value.representativeId = props.representativeId;

  emit('save', localImport.value);
}

//validation
watch(
  () => localImport.value.code,
  (newValue) => {
    validationModel.value.code = newValue || '';
  }
);

watch(
  () => selectedLastImportDate.value,
  (newValue) => {
    validationModel.value.lastImportDate = newValue || '';
  }
);

const validationModel = ref({
  code: localImport.value.code || '',
  lastImportDate: selectedLastImportDate.value || '',
});

const rules = computed(() => ({
  code: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  lastImportDate: {
    validDate: {
      $validator: () => {
        const validation = lastImportDatePickerRef.value?.getValidationState();
        return !validation || validation.isValid;
      },
      $message: $t('date.invalidDate'),
    },
  },
}));

const $v = useVuelidate(rules, validationModel);

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.representativeImport')}`;
}
</script>
